import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronRight, CircleHelp, CircleDashed } from "lucide-react";
import { computeCoverage, parseIntent, type IntentKind } from "@spectree/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { slotLabel, slotQuestion } from "@/lib/intent-labels";
import type { FogbugzBugBarResult } from "@ttc-spectree/toolbox-contract/fogbugz-formulator";
import type { InterviewCoverage, InterviewRouting } from "@/hooks/queries/use-fogbugz";
import { BugBarPanel, bugBarBlocks } from "./bug-bar-panel";

interface IntentReviewProps {
  /** Canonical `intent.md` as rendered by the server from the interview slots. */
  initialText: string;
  /**
   * Coverage the server last computed. Shown until the reporter edits the text,
   * after which the locally parsed coverage takes over as the live view.
   */
  serverCoverage?: InterviewCoverage | null;
  /**
   * The Bug bar the server computed on the last saved version of this report
   * (epic PI-SPEC-SW-E309). A bug case below the bar cannot be filed: the
   * panel names each missing rule, and Continue stays disabled until the
   * reporter edits the report, after which Continue saves and re-checks it.
   * Absent (older server, or not loaded yet) never blocks: the server is the gate.
   */
  bugBar?: FogbugzBugBarResult | null | undefined;
  /** True while the edited document is being persisted. */
  isSaving?: boolean;
  /** Error surfaced by the caller after a failed save (e.g. a server 400). */
  saveError?: string | null;
  /**
   * Where this report will be filed, as the server resolved it from the
   * reporter's pre-interview selection. Rendered read-only above the editor.
   *
   * Optional only because the document query resolves after the first render;
   * once it has, the API always supplies it.
   */
  routing?: InterviewRouting | undefined;
  onBack: () => void;
  /**
   * Called with the edited markdown, which the caller persists server-side.
   * Routing is deliberately not part of this callback: it was chosen on the
   * routing screen and this screen cannot change it.
   */
  onContinue: (intentText: string) => void;
  /** The Continue button's words. Defaults to the website's "Continue to Duplicate Check". */
  continueLabel?: string;
  /** The Back button's words. Defaults to "Back to Interview". */
  backLabel?: string;
}

/**
 * The originator-corrects-the-draft step.
 *
 * The reporter edits the assembled `intent.md` directly rather than being asked
 * more questions — that is the whole point of having an artifact (Resolved
 * Decision 1 in `docs/analysis/fogbugz-intent-interview.md`). Every keystroke is
 * re-parsed with the shared `parseIntent`, so a broken heading or a deleted
 * section is visible immediately instead of at submit time.
 *
 * The local parse is a convenience only. The server re-parses the same text and
 * is the authority on whether the document is acceptable; Continue hands the raw
 * markdown up to the caller, which performs that round-trip.
 */
export function IntentReview({
  initialText,
  serverCoverage,
  bugBar = null,
  isSaving = false,
  saveError = null,
  routing,
  onBack,
  onContinue,
  continueLabel = "Continue to Duplicate Check",
  backLabel = "Back to Interview",
}: IntentReviewProps) {
  const [text, setText] = useState(initialText);
  const [isDirty, setIsDirty] = useState(false);
  // Whether the reporter changed the text since the server last checked the Bug
  // bar. A failing result blocks Continue until there is something new to check.
  const [editedSinceCheck, setEditedSinceCheck] = useState(false);
  // The text as the reporter last sent it to be checked (Continue), and the
  // text now: a result that arrives while they kept typing describes the
  // older text, so their newer edit still counts.
  const checkedTextRef = useRef<string | null>(null);
  const textRef = useRef(text);
  useEffect(() => {
    textRef.current = text;
  }, [text]);
  useEffect(() => {
    setEditedSinceCheck(checkedTextRef.current !== null && textRef.current !== checkedTextRef.current);
  }, [bugBar]);

  // Seed the editor when the document arrives (the query resolves after the
  // first render). A reporter's in-progress edits are never clobbered: once the
  // text has been touched, later prop changes are ignored.
  const seededRef = useRef(initialText.length > 0);
  useEffect(() => {
    if (isDirty || seededRef.current || initialText.length === 0) {
      return;
    }
    seededRef.current = true;
    setText(initialText);
  }, [initialText, isDirty]);

  const parseResult = useMemo(() => parseIntent(text), [text]);

  // Coverage of what is actually in the editor right now. Falls back to the
  // server's last computation while the document is untouched and unparseable
  // (e.g. it has not loaded yet).
  //
  // The kind is taken from `serverCoverage`, never from the edited text. Kind is
  // routing-owned and fixed before the interview starts, and the server scores the
  // session's kind no matter what the document says. Reading it out of the editable
  // `kind:` line instead would let a deleted or mistyped line silently re-score a
  // feature against the bug slot set and disable Continue on a complete report.
  const localCoverage = useMemo(
    () => (parseResult.ok ? computeCoverage(parseResult.intent, serverCoverage?.kind) : null),
    [parseResult, serverCoverage?.kind]
  );

  // Both branches carry the kind so the two views of the same document can never
  // disagree about which slot set the counts are denominated by: the local view takes
  // it from the edited document's own frontmatter (`computeCoverage` resolves it), and
  // the server view from the payload that scored the interview.
  const coverage: CoverageView | null = localCoverage
    ? {
        kind: localCoverage.kind,
        filled: localCoverage.filled,
        unknown: localCoverage.unknown,
        remaining: [...localCoverage.requiredRemaining, ...localCoverage.optionalRemaining],
        isComplete: localCoverage.isComplete,
        totalSlots: localCoverage.totalSlots,
      }
    : serverCoverage
      ? {
          kind: serverCoverage.kind,
          filled: serverCoverage.filled,
          unknown: serverCoverage.unknown,
          remaining: [...serverCoverage.requiredRemaining, ...serverCoverage.optionalRemaining],
          isComplete: serverCoverage.isComplete,
          totalSlots: serverCoverage.totalSlots,
        }
      : null;

  const isCoverageComplete = parseResult.ok && (localCoverage?.isComplete ?? false);
  const belowBugBar = bugBarBlocks(bugBar);
  const canContinue =
    parseResult.ok &&
    isCoverageComplete &&
    !isSaving &&
    // Below the bar, Continue needs something new to check: an edit, or a
    // retry of a save that failed (the server never checked that text).
    !(belowBugBar && !editedSinceCheck && saveError === null);

  return (
    <div className="space-y-4">
      {routing && <RoutingSummary routing={routing} />}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-2">
          <label htmlFor="intent-editor" className="text-sm font-medium">
            Your report (intent.md)
          </label>
          <Textarea
            id="intent-editor"
            aria-label="Your report (intent.md)"
            value={text}
            spellCheck={false}
            onChange={(e) => {
              setIsDirty(true);
              setEditedSinceCheck(true);
              setText(e.target.value);
            }}
            className="min-h-[420px] font-mono text-xs leading-relaxed"
          />
          <p className="text-xs text-muted-foreground">
            This exact document is attached to the FogBugz case, and the case description is
            generated from it. Keep the <code>##</code> and <code>###</code> headings — they are
            what makes the report readable by the tools that pick it up later.
          </p>
        </div>

        <div className="space-y-3">{coverage && <CoveragePanel coverage={coverage} />}</div>
      </div>

      {!parseResult.ok && (
        <div
          className="space-y-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-sm"
          data-testid="intent-parse-errors"
          role="alert"
        >
          <div className="flex items-center gap-2 text-red-500 font-medium">
            <AlertCircle className="h-4 w-4" />
            <span>This report can no longer be read ({parseResult.errors.length})</span>
          </div>
          <ul className="space-y-1 pl-6 list-disc text-red-500/90">
            {parseResult.errors.map((parseError, i) => (
              <li key={`${parseError.code}-${String(i)}`}>{parseError.message}</li>
            ))}
          </ul>
        </div>
      )}

      {parseResult.warnings.length > 0 && (
        <div
          className="space-y-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm"
          data-testid="intent-parse-warnings"
        >
          <div className="flex items-center gap-2 text-yellow-500 font-medium">
            <AlertCircle className="h-4 w-4" />
            <span>Worth a look ({parseResult.warnings.length})</span>
          </div>
          <ul className="space-y-1 pl-6 list-disc text-muted-foreground">
            {parseResult.warnings.map((warning, i) => (
              <li key={`${warning.code}-${String(i)}`}>{warning.message}</li>
            ))}
          </ul>
        </div>
      )}

      {parseResult.ok && !isCoverageComplete && (
        <div
          className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm"
          data-testid="intent-incomplete-notice"
        >
          <p className="font-medium text-yellow-500 mb-1">Not ready to file yet</p>
          <p className="text-muted-foreground">
            Every section listed under &ldquo;Still needed&rdquo; needs something written under its
            heading. If you genuinely do not know one of the optional answers, leave the{" "}
            <code>_unknown_</code> marker in place together with the <code>&gt;</code> question
            line beneath it, and it will be filed as an open question instead.
          </p>
        </div>
      )}

      {bugBar && (
        <BugBarPanel
          bugBar={bugBar}
          hint={
            belowBugBar
              ? editedSinceCheck
                ? "Press Continue to save your changes and check again."
                : "A bug case needs these before it can be filed. Add them to your report above, then press Continue to check again."
              : undefined
          }
        />
      )}

      {saveError && (
        <div
          className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm"
          data-testid="intent-save-error"
          role="alert"
        >
          <AlertCircle className="h-4 w-4" />
          <span>{saveError}</span>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.08]">
        <Button variant="outline" onClick={onBack} disabled={isSaving}>
          {backLabel}
        </Button>
        <Button
          onClick={() => {
            // The text is now what the server checks next; a failing result
            // blocks Continue again until the reporter changes something (a
            // failed save re-enables it through `saveError`).
            setEditedSinceCheck(false);
            checkedTextRef.current = text;
            onContinue(text);
          }}
          disabled={!canContinue}
        >
          {isSaving ? "Saving..." : continueLabel}
          {!isSaving && <ChevronRight className="h-4 w-4 ml-1" />}
        </Button>
      </div>
    </div>
  );
}

/** One row of the routing summary: a FogBugz field and what it will be set to. */
interface RoutingField {
  label: string;
  value: string | undefined;
  /** What FogBugz does when the reporter left this field unset. */
  fallback: string;
  testId: string;
}

/**
 * Read-only statement of exactly what FogBugz will receive.
 *
 * This replaces the review-phase project picker, which is deleted rather than
 * relabelled: per the correction at the end of the E195 findings log, the
 * reporter never interacted with that control at all — it sat below the editor
 * pre-set to a default-project fallback option and required no interaction to
 * pass, so case #17054 was filed into the API key's default project, in that
 * project's default area, assigned to a departed employee. A clearer label
 * would not have helped; being asked on a screen of its own did.
 *
 * Read-only means read-only. Routing is chosen on the pre-interview routing
 * screen and nowhere else — a second editable surface here would reintroduce
 * two sources of truth for where the case lands. Rendered as a description list
 * so the pairing is structural rather than only visual.
 *
 * Fields the reporter left unset show what FogBugz itself will apply, so the
 * summary is never silent about a value the case will still end up with.
 */
function RoutingSummary({ routing }: { routing: InterviewRouting }) {
  const fields: RoutingField[] = [
    {
      label: "Project",
      value: routing.sProject,
      fallback: "Not set",
      testId: "routing-summary-project",
    },
    {
      label: "Area",
      value: routing.sArea,
      fallback: "The project\u2019s default area",
      testId: "routing-summary-area",
    },
    {
      label: "Milestone",
      value: routing.sFixFor,
      fallback: "The project\u2019s default milestone",
      testId: "routing-summary-milestone",
    },
    {
      label: "Category",
      value: routing.sCategory,
      fallback: "FogBugz default (Bug)",
      testId: "routing-summary-category",
    },
    {
      label: "Assigned To",
      value: routing.sPersonAssignedTo,
      fallback: "The project\u2019s primary contact",
      testId: "routing-summary-assignee",
    },
    {
      label: "Priority",
      value: routing.sPriority,
      fallback: "From the severity you reported",
      testId: "routing-summary-priority",
    },
    {
      label: "Tags",
      value: routing.tags && routing.tags.length > 0 ? routing.tags.join(", ") : undefined,
      fallback: "None",
      testId: "routing-summary-tags",
    },
    {
      label: "Version",
      value: routing.version === "" ? undefined : routing.version,
      fallback: "None",
      testId: "routing-summary-version",
    },
  ];

  return (
    <div
      className="p-4 border border-white/[0.08] rounded-lg space-y-3"
      data-testid="intent-routing-summary"
    >
      <div className="space-y-1">
        <span className="text-sm font-medium">Where this will be filed</span>
        <p className="text-xs text-muted-foreground">
          Exactly what FogBugz will receive. To change any of it, start again from the routing
          screen.
        </p>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {fields.map((field) => (
          <div key={field.label} className="space-y-0.5" data-testid={field.testId}>
            <dt className="text-xs text-muted-foreground">{field.label}</dt>
            <dd
              className={cn(
                "text-sm",
                field.value ? "font-medium" : "text-muted-foreground italic"
              )}
            >
              {field.value ?? field.fallback}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Flattened coverage shape rendered by {@link CoveragePanel}. */
interface CoverageView {
  /**
   * The slot set these counts are scored against. Carried so the locally parsed view
   * and the server's view are comparable rather than two unlabelled totals.
   */
  kind: IntentKind;
  filled: readonly string[];
  unknown: readonly string[];
  remaining: readonly string[];
  isComplete: boolean;
  totalSlots: number;
}

/**
 * Filled / unknown / still-needed breakdown of the document in the editor.
 *
 * The remaining list carries each slot's non-engineer question, so the panel
 * tells the reporter what to write rather than only that something is absent.
 */
function CoveragePanel({ coverage }: { coverage: CoverageView }) {
  const answered = Math.max(coverage.totalSlots - coverage.remaining.length, 0);

  return (
    <div className="p-4 border border-white/[0.08] rounded-lg space-y-3" data-testid="intent-coverage-panel">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Coverage</span>
        <span
          className={cn(
            "text-xs px-2 py-0.5 rounded",
            coverage.isComplete
              ? "bg-green-500/10 text-green-500"
              : "bg-yellow-500/10 text-yellow-500"
          )}
          data-testid="intent-coverage-count"
        >
          {answered} of {coverage.totalSlots}
        </span>
      </div>

      {coverage.remaining.length > 0 && (
        <div className="space-y-1" data-testid="intent-coverage-remaining">
          <div className="flex items-center gap-1.5 text-xs font-medium text-yellow-500">
            <CircleDashed className="h-3.5 w-3.5" />
            <span>Still needed</span>
          </div>
          <ul className="space-y-1.5">
            {coverage.remaining.map((id) => (
              <li key={id} className="text-xs">
                <span className="font-medium">{slotLabel(id)}</span>
                {slotQuestion(id) && (
                  <span className="block text-muted-foreground">{slotQuestion(id)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {coverage.unknown.length > 0 && (
        <div className="space-y-1" data-testid="intent-coverage-unknown">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <CircleHelp className="h-3.5 w-3.5" />
            <span>Marked unknown</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {coverage.unknown.map((id) => slotLabel(id)).join(", ")}
          </p>
        </div>
      )}

      {coverage.filled.length > 0 && (
        <div className="space-y-1" data-testid="intent-coverage-filled">
          <div className="flex items-center gap-1.5 text-xs font-medium text-green-500">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Answered</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {coverage.filled.map((id) => slotLabel(id)).join(", ")}
          </p>
        </div>
      )}
    </div>
  );
}
